import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Logo from "../../assets/logo_horizontal-oficial.png";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { buildTemplatePreview, extractTemplateVariableKeys } from "./templatePreviewUtils";

const useStyles = makeStyles(() => ({
  searchField: {
    marginBottom: 8,
  },
  templateList: {
    maxHeight: 360,
    overflow: "auto",
    border: "1px solid #e0e0e0",
  },
  listCheckbox: {
    minWidth: 36,
  },
  variablesBox: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  variablesTitle: {
    marginBottom: 8,
  },
  phoneMockup: {
    background: "#eeeeee",
    minHeight: 320,
    border: "12px solid #0b708c",
    borderBottom: "none",
    borderTopRightRadius: 40,
    borderTopLeftRadius: 40,
    paddingBottom: 10,
  },
  phoneHeader: {
    background: "#ffffff",
    height: 60,
    borderTopRightRadius: 28,
    borderTopLeftRadius: 28,
    padding: 12,
    marginBottom: 12,
    boxShadow: "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  phoneLogo: {
    maxWidth: 140,
    maxHeight: 36,
    objectFit: "contain",
  },
  messageBubble: {
    background: "#fff",
    padding: 10,
    minHeight: 70,
    borderRadius: 12,
    maxWidth: "85%",
    boxShadow: "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
    margin: "0 auto 8px 10px",
    wordBreak: "break-word",
  },
  previewHeader: {
    fontWeight: 700,
    marginBottom: 4,
  },
  previewBody: {
    whiteSpace: "pre-wrap",
  },
  previewFooter: {
    fontSize: "0.8rem",
    color: "#757575",
    marginTop: 4,
  },
  previewButton: {
    marginTop: 8,
    borderRadius: 8,
    textTransform: "none",
  },
}));

export default function SendTemplateModal({
  open,
  onClose,
  ticketId = null,
  whatsappId = null,
  queueId = null,
  onSent,
  embedded = false,
  onTemplateChange,
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [variables, setVariables] = useState({});

  useEffect(() => {
    if (!open) return;

    let isMounted = true;
    const loadTemplates = async () => {
      setLoading(true);
      try {
        let data;
        if (ticketId) {
          ({ data } = await api.get(`/messages/${ticketId}/templates`));
        } else if (whatsappId) {
          ({ data } = await api.get("/whatsappTemplate/attendance/list", {
            params: { whatsappId, queueId: queueId || undefined },
          }));
        } else {
          data = { templates: [] };
        }
        if (!isMounted) return;
        setTemplates(data?.templates || []);
      } catch (err) {
        toastError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadTemplates();
    return () => {
      isMounted = false;
    };
  }, [open, ticketId, whatsappId, queueId]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelected(null);
      setVariables({});
    }
  }, [open]);

  const filteredTemplates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((t) =>
      [t.displayName, t.name, t.preview].some((v) =>
        String(v || "").toLowerCase().includes(term)
      )
    );
  }, [templates, search]);

  const preview = useMemo(() => {
    if (!selected) return { headerText: "", bodyText: "", footerText: "", buttons: [] };
    return buildTemplatePreview(selected.components, variables);
  }, [selected, variables]);

  const handleSelectTemplate = (template) => {
    setSelected(template);
    const keys = template.variableKeys?.length
      ? template.variableKeys
      : extractTemplateVariableKeys(template.components);
    const initial = {};
    keys.forEach((key) => {
      initial[key] = "";
    });
    setVariables(initial);
    onTemplateChange?.({ template, variables: initial });
  };

  const handleVariableChange = (key, value) => {
    setVariables((prev) => {
      const next = { ...prev, [key]: value };
      if (selected) onTemplateChange?.({ template: selected, variables: next });
      return next;
    });
  };

  const handleSend = async () => {
    if (!selected) {
      toast.error(i18n.t("sendTemplateModal.selectTemplate"));
      return;
    }

    const keys = selected.variableKeys?.length
      ? selected.variableKeys
      : extractTemplateVariableKeys(selected.components);
    const missing = keys.filter((key) => !String(variables[key] || "").trim());
    if (missing.length > 0) {
      toast.error(i18n.t("sendTemplateModal.fillVariables"));
      return;
    }

    if (!ticketId) return;

    setSending(true);
    try {
      const { data } = await api.post(`/messages/${ticketId}/template`, {
        templateName: selected.name,
        language: selected.language,
        variables,
        components: selected.components,
      });
      toast.success(
        data?.message ? i18n.t(data.message) : i18n.t("sendTemplateModal.sentToMeta")
      );
      onSent?.();
      onClose?.();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const content = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={i18n.t("sendTemplateModal.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={classes.searchField}
        />
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <List dense className={classes.templateList}>
            {filteredTemplates.map((template) => (
              <ListItem
                key={`${template.name}-${template.language}`}
                button
                onClick={() => handleSelectTemplate(template)}
                selected={selected?.name === template.name && selected?.language === template.language}
              >
                <ListItemIcon className={classes.listCheckbox}>
                  <Checkbox
                    edge="start"
                    checked={selected?.name === template.name && selected?.language === template.language}
                    tabIndex={-1}
                    disableRipple
                    color="primary"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={template.displayName || template.name}
                  secondary={template.preview}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItem>
            ))}
            {!loading && filteredTemplates.length === 0 && (
              <ListItem>
                <ListItemText primary={i18n.t("sendTemplateModal.notFound")} />
              </ListItem>
            )}
          </List>
        )}
      </Grid>

      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" className={classes.variablesTitle}>
          {i18n.t("sendTemplateModal.fillVariablesTitle")}
        </Typography>
        {!selected ? (
          <Typography color="textSecondary">{i18n.t("sendTemplateModal.selectTemplateHint")}</Typography>
        ) : (
          <Box className={classes.variablesBox}>
            {(selected.variableKeys?.length
              ? selected.variableKeys
              : extractTemplateVariableKeys(selected.components)
            ).map((key) => (
              <TextField
                key={key}
                fullWidth
                size="small"
                variant="outlined"
                label={key}
                value={variables[key] || ""}
                onChange={(e) => handleVariableChange(key, e.target.value)}
              />
            ))}
            {(selected.variableKeys?.length
              ? selected.variableKeys
              : extractTemplateVariableKeys(selected.components)
            ).length === 0 && (
              <Typography color="textSecondary">{i18n.t("sendTemplateModal.noVariables")}</Typography>
            )}
          </Box>
        )}
      </Grid>

      <Grid item xs={12} md={4}>
        <Box className={classes.phoneMockup}>
          <Box className={classes.phoneHeader}>
            <img src={Logo} alt="logo" className={classes.phoneLogo} />
          </Box>
          <Box className={classes.messageBubble}>
            {preview.headerText && (
              <Typography className={classes.previewHeader}>{preview.headerText}</Typography>
            )}
            <Typography className={classes.previewBody}>
              {preview.bodyText || i18n.t("sendTemplateModal.previewEmpty")}
            </Typography>
            {preview.footerText && (
              <Typography className={classes.previewFooter}>{preview.footerText}</Typography>
            )}
            {preview.buttons?.map((btn) => (
              <Button
                key={btn.text}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.previewButton}
                disabled
              >
                {btn.text}
              </Button>
            ))}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );

  if (embedded) {
    return content;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{i18n.t("sendTemplateModal.title")}</DialogTitle>
      <DialogContent dividers>{content}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          {i18n.t("sendTemplateModal.cancel")}
        </Button>
        <Button onClick={handleSend} variant="contained" color="primary" disabled={sending || !selected || !ticketId}>
          {sending ? <CircularProgress size={20} color="inherit" /> : i18n.t("sendTemplateModal.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { buildTemplatePreview, extractTemplateVariableKeys };
